import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserPermissions, PERMISSIONS } from '../utils/permissionUtils';
import PermissionToast from './PermissionToast';
import { 
  UserGroupIcon,
  UsersIcon,
  StarIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  CurrencyRupeeIcon,
  DocumentTextIcon,
  BuildingOfficeIcon,
  TruckIcon,
  TicketIcon,
  BriefcaseIcon,
  BuildingStorefrontIcon,
  UserIcon,
  ChartBarIcon,
  CogIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ShieldCheckIcon,
  ClockIcon,
  CalendarDaysIcon,
  XCircleIcon,
  DocumentArrowUpIcon
} from '@heroicons/react/24/outline';

const DashboardCards = ({ stats, pendingReviewsCount, unpaidInvoicesCount, unpaidInvoicesAmount, onRefresh }) => {
  const navigate = useNavigate();
  const [userPermissions, setUserPermissions] = useState({});
  const [loadingPermissions, setLoadingPermissions] = useState(true);
  const [permissionToast, setPermissionToast] = useState({ show: false, message: '' });
  const [collapsedSections, setCollapsedSections] = useState({
    '🚀 Quick Actions': false,
    '👥 User Management': false,
    '📅 Reservations': false,
    '🅿️ Parking Management': true,
    '💰 Financial': true,
    '🎫 Pass Management': true,
    '⚙️ Operations': true
  });

  useEffect(() => {
    const loadUserPermissions = async () => {
      try {
        const currentUserStr = localStorage.getItem('currentUser');
        console.log('Dashboard - Raw localStorage currentUser:', currentUserStr);
        
        const currentUser = JSON.parse(currentUserStr || '{}');
        const userId = currentUser.uid || currentUser.id || currentUser.firebaseUid;
        
        console.log('Dashboard - Current user from localStorage:', currentUser);
        console.log('Dashboard - User ID being used:', userId);
        console.log('Dashboard - Available user properties:', Object.keys(currentUser));
        console.log('Dashboard - All localStorage keys:', Object.keys(localStorage));
        
        console.log('Loading permissions for user:', userId);
        
        if (userId) {
          const permissions = await getUserPermissions(userId);
          console.log('Loaded permissions:', permissions);
          console.log('Permissions type:', typeof permissions);
          console.log('Permissions keys:', Object.keys(permissions));
          console.log('QuickActions permissions:', permissions.quickActions);
          setUserPermissions(permissions);
        }
      } catch (error) {
        console.error('Error loading user permissions:', error);
      } finally {
        setLoadingPermissions(false);
      }
    };

    loadUserPermissions();
  }, []);

  const hasPermission = (permission) => {
    if (!permission) return true;
    
    // If permissions are still loading, deny access
    if (loadingPermissions) {
      console.log('Permissions still loading, denying access');
      return false;
    }
    
    // If no permissions loaded, deny access
    if (Object.keys(userPermissions).length === 0) {
      console.log('No permissions loaded, denying access');
      return false;
    }
    
    console.log('Checking permission:', permission);
    console.log('User permissions:', userPermissions);
    
    const parts = permission.split('.');
    if (parts.length === 2) {
      const [category, perm] = parts;
      console.log('Permission parts - category:', category, 'perm:', perm);
      console.log('Category permissions:', userPermissions[category]);
      console.log('Specific permission value:', userPermissions[category]?.[perm]);
      const result = userPermissions[category]?.[perm] === true;
      console.log('Permission result:', result);
      return result;
    }
    
    return false;
  };





  // Quick Actions - Most frequently used features (ordered by priority)
  const quickActionsCards = [
    {
      title: 'Analytics Hub',
      value: '📊',
      description: 'All analytics dashboards and insights',
      icon: ChartBarIcon,
      color: 'text-white',
      bgColor: 'bg-gradient-to-r from-purple-600 to-purple-700',
      onClick: () => navigate('/analytics'),
      priority: 'high',
      permission: PERMISSIONS.QUICK_ACTIONS_ANALYTICS_HUB
    },
    {
      title: 'All Reservations',
      value: stats.totalReservations,
      description: 'View and manage all reservations',
      icon: CalendarIcon,
      color: 'text-white',
      bgColor: 'bg-gradient-to-r from-blue-600 to-blue-700',
      onClick: () => navigate('/reservations'),
      priority: 'high',
      permission: PERMISSIONS.QUICK_ACTIONS_ALL_RESERVATIONS
    },
    {
      title: 'Total Earnings',
      value: `₹${stats.totalEarnings.toLocaleString()}`,
      description: 'Total revenue generated',
      icon: CurrencyDollarIcon,
      color: 'text-white',
      bgColor: 'bg-gradient-to-r from-green-600 to-green-700',
      onClick: () => navigate('/transactions'),
      priority: 'high',
      permission: PERMISSIONS.QUICK_ACTIONS_TOTAL_EARNINGS
    },
    {
      title: 'Pending Reviews',
      value: pendingReviewsCount !== undefined ? (
        <span className="flex items-center">
          <span className="text-2xl font-bold">{pendingReviewsCount}</span>
          {pendingReviewsCount > 0 && (
            <ExclamationTriangleIcon className="w-5 h-5 ml-2 text-yellow-400" />
          )}
        </span>
      ) : stats.reviews,
      description: 'Reviews awaiting approval',
      icon: StarIcon,
      color: 'text-white',
      bgColor: pendingReviewsCount > 0 ? 'bg-gradient-to-r from-yellow-500 to-orange-500' : 'bg-gradient-to-r from-gray-500 to-gray-600',
      onClick: () => navigate('/pending-reviews'),
      priority: 'high',
      permission: PERMISSIONS.QUICK_ACTIONS_PENDING_REVIEWS
    },
    {
      title: 'Unpaid Invoices',
      value: unpaidInvoicesCount !== undefined ? (
        <div className="flex items-center">
          <span className="text-2xl font-bold">{unpaidInvoicesCount}</span>
          {unpaidInvoicesAmount > 0 && (
            <span className="text-sm font-medium ml-2">
              (₹{unpaidInvoicesAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
            </span>
          )}
          {unpaidInvoicesCount > 0 && (
            <ExclamationTriangleIcon className="w-5 h-5 ml-2 text-red-400" />
          )}
        </div>
      ) : stats.invoices,
      description: 'Invoices pending payment',
      icon: DocumentTextIcon,
      color: 'text-white',
      bgColor: unpaidInvoicesCount > 0 ? 'bg-gradient-to-r from-red-500 to-red-600' : 'bg-gradient-to-r from-gray-500 to-gray-600',
      onClick: () => navigate('/invoices'),
      priority: 'high',
      permission: PERMISSIONS.QUICK_ACTIONS_UNPAID_INVOICES
    }
  ];

  // User Management Section (ordered by user type importance)
  const userManagementCards = [
    {
      title: 'Parking Users',
      value: stats.parkingUsers,
      description: 'Manage parking users and send WhatsApp referrals',
      icon: UsersIcon,
      color: 'text-white',
      bgColor: 'bg-gradient-to-r from-blue-600 to-blue-700',
      onClick: () => navigate('/parking-user-list'),
      permission: PERMISSIONS.USER_MANAGEMENT_PARKING_USERS
    },
    {
      title: 'Upload Businesses',
      value: '📤',
      description: 'Upload Excel files or manually add businesses',
      icon: DocumentArrowUpIcon,
      color: 'text-white',
      bgColor: 'bg-gradient-to-r from-emerald-600 to-emerald-700',
      onClick: () => navigate('/manual-user-list'),
      permission: PERMISSIONS.USER_MANAGEMENT_MANUAL_USER_LIST
    },
    {
      title: 'Business List & WhatsApp',
      value: '📋',
      description: 'View all businesses and send WhatsApp messages',
      icon: DocumentTextIcon,
      color: 'text-white',
      bgColor: 'bg-gradient-to-r from-purple-600 to-purple-700',
      onClick: () => navigate('/manual-business-list'),
      permission: PERMISSIONS.USER_MANAGEMENT_MANUAL_BUSINESS_LIST
    },
    {
      title: 'Parking Owners',
      value: stats.parkingOwners,
      description: 'Total parking and spaces owners',
      icon: UserGroupIcon,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      onClick: () => navigate('/parking-owners-list'),
      permission: PERMISSIONS.USER_MANAGEMENT_PARKING_OWNERS
    },
    {
      title: 'Partners',
      value: stats.partners,
      description: 'Total partners and deal providers',
      icon: BriefcaseIcon,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      onClick: () => navigate('/partners-list'),
      permission: PERMISSIONS.USER_MANAGEMENT_PARTNERS
    },
    {
      title: 'Kerblet MealDesk Vendors',
      value: stats.mealVendors,
      description: 'Total meal vendors',
      icon: BuildingStorefrontIcon,
      color: 'text-teal-600',
      bgColor: 'bg-teal-50',
      onClick: () => navigate('/kerbletmeal/vendors'),
      permission: PERMISSIONS.USER_MANAGEMENT_MEAL_VENDORS
    },
    {
      title: 'Kerblet MealDesk Customers',
      value: stats.mealCustomers,
      description: 'Total meal customers',
      icon: UserIcon,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      onClick: () => navigate('/kerbletmeal/customers'),
      permission: PERMISSIONS.USER_MANAGEMENT_MEAL_CUSTOMERS
    },
    {
      title: 'Role Management',
      value: '🔐',
      description: 'Manage user roles and permissions',
      icon: ShieldCheckIcon,
      color: 'text-white',
      bgColor: 'bg-gradient-to-r from-red-600 to-red-700',
      onClick: () => navigate('/role-management'),
      permission: PERMISSIONS.USER_MANAGEMENT_ROLE_MANAGEMENT
    }
  ];

  // Reservations Section - Logical workflow order
  const reservationsCards = [
    {
      title: 'Reserved (Confirmation Pending)',
      value: stats.reservedReservations || 0,
      description: 'Reservations pending confirmation',
      icon: CalendarDaysIcon,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      onClick: () => navigate('/reservations?state=reserved'),
      permission: PERMISSIONS.RESERVATIONS_RESERVED
    },
    {
      title: 'Confirmed (Checkin Pending)',
      value: stats.confirmedReservations || 0,
      description: 'Confirmed reservations',
      icon: CheckCircleIcon,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      onClick: () => navigate('/reservations?state=confirmed'),
      permission: PERMISSIONS.RESERVATIONS_CONFIRMED
    },
    {
      title: 'Active Reservations',
      value: stats.checkinReservations || 0,
      description: 'Currently checked in reservations',
      icon: CalendarIcon,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      onClick: () => navigate('/reservations?state=checkin'),
      permission: PERMISSIONS.RESERVATIONS_ACTIVE
    },
    {
      title: 'Completed Reservations',
      value: stats.finishedReservations || 0,
      description: 'Completed reservations',
      icon: ClockIcon,
      color: 'text-gray-600',
      bgColor: 'bg-gray-50',
      onClick: () => navigate('/reservations?state=finished'),
      permission: PERMISSIONS.RESERVATIONS_COMPLETED
    },
    {
      title: 'Cancelled',
      value: stats.canceledReservations || 0,
      description: 'Cancelled reservations',
      icon: XCircleIcon,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      onClick: () => navigate('/reservations?state=canceled'),
      permission: PERMISSIONS.RESERVATIONS_CANCELLED
    }
  ];

  // Parking Management Section (ordered by operational priority)
  const parkingManagementCards = [
    {
      title: 'Total Spaces',
      value: stats.parkingSpaces,
      description: 'Total parking spaces',
      icon: BuildingOfficeIcon,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      onClick: () => navigate('/spaces-list?filter=all'),
      permission: PERMISSIONS.PARKING_MANAGEMENT_TOTAL_SPACES
    },
    {
      title: 'Available Spaces',
      value: stats.freeParking,
      description: 'Free parking spaces (Free = true)',
      icon: TruckIcon,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      onClick: () => navigate('/spaces-list?filter=available'),
      permission: PERMISSIONS.PARKING_MANAGEMENT_AVAILABLE_SPACES
    },
    {
      title: 'Disabled Spaces',
      value: stats.disabledSpaces,
      description: 'New Space Awaiting Approvals',
      icon: XCircleIcon,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      onClick: () => navigate('/spaces-list?filter=disabled'),
      permission: PERMISSIONS.PARKING_MANAGEMENT_DISABLED_SPACES
    }
  ];

  // Financial Section (ordered by financial importance)
  const financialCards = [
    {
      title: 'Total Transactions',
      value: stats.totalTransactions,
      description: 'Total financial transactions',
      icon: CurrencyRupeeIcon,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      onClick: () => navigate('/transactions'),
      permission: PERMISSIONS.FINANCIAL_TOTAL_TRANSACTIONS
    },
    {
      title: 'Total Invoices',
      value: stats.invoices,
      description: 'Total invoices generated',
      icon: DocumentTextIcon,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      onClick: () => navigate('/invoices'),
      permission: PERMISSIONS.FINANCIAL_TOTAL_INVOICES
    },
    {
      title: 'Total Earnings',
      value: `₹${stats.totalEarnings.toLocaleString()}`,
      description: 'Total revenue generated',
      icon: CurrencyDollarIcon,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      onClick: () => navigate('/transactions'),
      permission: PERMISSIONS.FINANCIAL_TOTAL_EARNINGS
    }
  ];

  // Pass Management Section (ordered by pass type)
  const passManagementCards = [
    {
      title: 'Total Passes',
      value: stats.totalPasses,
      description: 'Total passes issued',
      icon: TicketIcon,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      permission: PERMISSIONS.PASS_MANAGEMENT_TOTAL_PASSES
    },
    {
      title: 'Multi-use Passes',
      value: stats.multiUsePasses,
      description: 'Passes for multiple uses',
      icon: TicketIcon,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      onClick: () => navigate('/multi-use-passes'),
      permission: PERMISSIONS.PASS_MANAGEMENT_MULTI_USE_PASSES
    },
    {
      title: 'One-time Passes',
      value: stats.oneTimePasses,
      description: 'Passes for single use',
      icon: TicketIcon,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      onClick: () => navigate('/one-time-passes'),
      permission: PERMISSIONS.PASS_MANAGEMENT_ONE_TIME_PASSES
    },
    {
      title: 'Partner Discount Coupons',
      value: stats.dealCoupons,
      description: 'Discount coupons from partners',
      icon: TicketIcon,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      onClick: () => navigate('/partner-deal-coupons'),
      permission: PERMISSIONS.PASS_MANAGEMENT_PARTNER_DISCOUNT_COUPONS
    },
    {
      title: 'Perks Deal Vouchers',
      value: stats.dealVouchers,
      description: 'Deal vouchers from pass collection',
      icon: TicketIcon,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      onClick: () => navigate('/perks-deal-vouchers'),
      permission: PERMISSIONS.PASS_MANAGEMENT_PERKS_DEAL_VOUCHERS
    }
  ];

  // Operations Section (ordered by operational frequency)
  const operationsCards = [
    {
      title: 'Kerblet MealDesk Schedule',
      value: 'Plan',
      description: 'Plan Meal Schedule For Customers',
      icon: CalendarIcon,
      color: 'text-teal-600',
      bgColor: 'bg-teal-50',
      onClick: () => navigate('/kerbletmeal/schedule'),
      permission: PERMISSIONS.OPERATIONS_MEAL_DESK_SCHEDULE
    },
    {
      title: 'Field Day Planner',
      value: 'Manage',
      description: 'Plan field activities',
      icon: CogIcon,
      color: 'text-gray-600',
      bgColor: 'bg-gray-50',
      onClick: () => navigate('/field-day-planner'),
      permission: PERMISSIONS.OPERATIONS_FIELD_DAY_PLANNER
    }
  ];

  // Helper function to render a section with title and cards
  const renderSection = (title, cards, gridCols = 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4', sectionColor = 'border-blue-200', useFlex = false) => {
    const isCollapsed = collapsedSections[title];
    
    const toggleSection = () => {
      setCollapsedSections(prev => ({
        ...prev,
        [title]: !prev[title]
      }));
    };

    // Filter cards based on permissions
    const filteredCards = cards.filter(card => {
      const hasAccess = hasPermission(card.permission);
      console.log(`Card: ${card.title}, Permission: ${card.permission}, Has Access: ${hasAccess}`);
      return hasAccess;
    });
    
    console.log(`Section ${title}:`, {
      totalCards: cards.length,
      filteredCards: filteredCards.length,
      permissions: cards.map(card => ({ title: card.title, permission: card.permission, hasPermission: hasPermission(card.permission) }))
    });

    return (
      <div className="space-y-3">
        <div 
          className={`border-l-4 ${sectionColor} pl-3 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors`}
          onClick={toggleSection}
        >
          <div className="flex items-center justify-between">
            <h3 className="text-base font-bold text-gray-800">{title}</h3>
            {isCollapsed ? (
              <ChevronRightIcon className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronDownIcon className="w-5 h-5 text-gray-500" />
            )}
          </div>
        </div>
        {!isCollapsed && (
          <div className={useFlex ? 'flex flex-wrap gap-4' : `grid ${gridCols} gap-4`}>
            {filteredCards.map((card, index) => (
              <div 
                key={index} 
                className={`bg-white rounded-lg shadow-sm border border-gray-200 p-3 transition-all duration-300 group ${
                  useFlex ? 'flex-1 min-w-[250px]' : 'w-auto'
                } ${
                  card.onClick 
                    ? 'cursor-pointer select-none hover:shadow-lg hover:border-blue-300 hover:scale-102' 
                    : 'hover:shadow-md hover:scale-101'
                } ${card.priority === 'high' ? 'ring-2 ring-blue-200' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (card.onClick && hasPermission(card.permission)) {
                    console.log(`Card clicked: ${card.title}`);
                    console.log(`Card permission: ${card.permission}`);
                    console.log(`Permission check result: ${hasPermission(card.permission)}`);
                    card.onClick();
                  } else if (!hasPermission(card.permission)) {
                    console.log(`Permission denied for: ${card.title}`);
                    console.log(`Card permission: ${card.permission}`);
                    console.log(`Permission check result: ${hasPermission(card.permission)}`);
                    setPermissionToast({ 
                      show: true, 
                      message: `You don't have permission to access ${card.title}` 
                    });
                  }
                }}
                style={{ userSelect: card.onClick ? 'none' : 'auto' }}
              >
                <div className="flex items-center justify-between h-full">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <div className={`p-2 rounded-lg ${card.bgColor} ${card.color.includes('white') ? 'shadow-lg' : ''} flex-shrink-0`}>
                      <card.icon className={`w-5 h-5 ${card.color}`} />
                    </div>
                    <div className="flex-1 min-w-0 pr-2">
                      <p className="text-sm font-bold text-gray-800 mb-1 break-words">
                        {card.title}
                      </p>
                      <p className="text-xs text-gray-500 leading-tight break-words">
                        {card.description}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 flex-shrink-0">
                    <div className={`px-2 py-1 rounded-full ${card.bgColor} ${card.color} border border-current`}>
                      <p className="text-sm font-bold">
                        {card.value}
                      </p>
                    </div>
                    {card.onClick && (
                      <div className="text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loadingPermissions) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Debug: Show all permissions
  console.log('Dashboard - All user permissions:', userPermissions);
  console.log('Dashboard - Permission keys:', Object.keys(userPermissions));
  console.log('Dashboard - Loading permissions:', loadingPermissions);
  
  // Test specific permissions
  if (userPermissions.quickActions) {
    console.log('Dashboard - QuickActions analyticsHub:', userPermissions.quickActions.analyticsHub);
    console.log('Dashboard - QuickActions allReservations:', userPermissions.quickActions.allReservations);
  }

  return (
    <>
      <div className="space-y-6">
        {/* Quick Actions - High priority items */}
        {renderSection('🚀 Quick Actions', quickActionsCards, 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4', 'border-green-400', true)}
        
        {/* User Management */}
        {renderSection('👥 User Management', userManagementCards, 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4', 'border-blue-400')}
        
        {/* Reservations - Grouped as requested */}
        {renderSection('📅 Reservations', reservationsCards, 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4', 'border-purple-400')}
        
        {/* Parking Management */}
        {renderSection('🅿️ Parking Management', parkingManagementCards, 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4', 'border-indigo-400')}
        
        {/* Financial */}
        {renderSection('💰 Financial', financialCards, 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4', 'border-green-400')}
        
        {/* Pass Management */}
        {renderSection('🎫 Pass Management', passManagementCards, 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4', 'border-orange-400')}
        
        {/* Operations */}
        {renderSection('⚙️ Operations', operationsCards, 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4', 'border-gray-400')}
      </div>
      
      <PermissionToast 
        message={permissionToast.message}
        isVisible={permissionToast.show}
        onClose={() => setPermissionToast({ show: false, message: '' })}
      />
    </>
  );
};

export default DashboardCards; 